
import moment from 'moment'
import { mapActions } from 'vuex'
import { effectiveModelStart } from '@/utils/modelStart.js'
import { parseAmount, formatAmount, formatFullAmount } from '@/utils/compactAmount.js'
import { DEFAULT_GRID, MAX_GRID_POINTS, modelDates, buildGrid, buildPoints, planPeriods, historicalSpend, historicalMix, normalizeMix } from '@/utils/budgetSimulator.js'

export default {
  props: {
    client: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    channels: {
      type: Object,
      required: true
    },
    report: {
      type: Object,
      required: false,
      default: null
    },
    base: {
      type: Object,
      required: false,
      default: null
    },
    // Rewrites the base Budget Simulator instead of creating a new one.
    edit: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      name: '',
      dateFrom: '',
      dateFromMenu: false,
      dateTo: '',
      dateToMenu: false,
      // Asked rather than assumed: a new simulation starts with no mode until one is picked.
      mixMode: null,
      referenceBudget: 0,
      referenceTouched: false,
      // While the reference budget is being edited it shows in full; once left, in compact form.
      referenceFocused: false,
      minPct: DEFAULT_GRID.minPct,
      maxPct: DEFAULT_GRID.maxPct,
      points: DEFAULT_GRID.points,
      maxPoints: MAX_GRID_POINTS,
      mix: {},
      mixTouched: false,
      formKey: 0,
      saving: false,
      error: ''
    }
  },
  computed: {
    referenceModelConfig () {
      if (this.client.plan_reference_model) {
        return this.client.plan_reference_model.config
      }
      return this.client.reference_model.config
    },
    brandDates () {
      return modelDates(this.client, this.referenceModelConfig)
    },
    modelFrequency () {
      return this.referenceModelConfig.saturation_granularity === 'W' ? 'week' : 'month'
    },
    startDate () {
      return effectiveModelStart(this.brandDates.start, this.modelFrequency, this.client.date_created ? this.client.date_created.toDate() : null)
    },
    endDate () {
      return this.brandDates.end
    },
    // The last date with real data; past it, up to endDate, the model's data is imputed.
    lastDataDate () {
      const configDates = this.referenceModelConfig.dates || {}
      return this.brandDates.last_data || configDates.last_data || this.endDate
    },
    spendUnitName () {
      return this.referenceModelConfig.invest_unit_name || 'USD'
    },
    channelNames () {
      return Object.keys(this.channels)
    },
    datesInvalid () {
      return !!(this.dateFrom && this.dateTo && this.dateTo <= this.dateFrom)
    },
    setupComplete () {
      return !!(this.mixMode && this.dateFrom && this.dateTo && !this.datesInvalid)
    },
    mixModeHint () {
      if (this.mixMode === 'frozen') return this.$t('components.budgetSimulator.CreateBudgetSimulation.frozenDesc')
      if (this.mixMode === 'free') return this.$t('components.budgetSimulator.CreateBudgetSimulation.freeDesc')
      return this.$t('components.budgetSimulator.CreateBudgetSimulation.pickMode')
    },
    periods () {
      return planPeriods(this.dateFrom, this.dateTo, this.modelFrequency, this.brandDates.all, this.endDate)
    },
    periodHint () {
      if (!this.dateFrom || !this.dateTo) return ''
      const key = this.modelFrequency === 'week' ? 'periodWeeks' : 'periodMonths'
      return this.$tc(`components.budgetSimulator.CreateBudgetSimulation.${key}`, this.periods.length, { count: this.periods.length })
    },
    spend () {
      return historicalSpend(this.report, this.channelNames, this.periods, this.modelFrequency)
    },
    historical () {
      return historicalMix(this.spend)
    },
    rangeValid () {
      return Number(this.minPct) > 0 && Number(this.maxPct) > Number(this.minPct)
    },
    grid () {
      if (!(Number(this.referenceBudget) > 0) || !this.rangeValid) return []
      return buildGrid(Number(this.referenceBudget), Number(this.minPct), Number(this.maxPct), Number(this.points))
    },
    mixTotal () {
      return this.channelNames.reduce((acc, channel) => acc + (Number(this.mix[channel]) || 0), 0)
    },
    // Valid when the total reads 100.0%, as it is shown: the arrows of a field snap a share to tenths,
    // and hundredths nobody can see are not left to block the form. The saved mix is evened out to
    // exactly 100% (see create).
    mixValid () {
      const nonNegative = this.channelNames.every(channel => (Number(this.mix[channel]) || 0) >= 0)
      return nonNegative && Math.round(this.mixTotal * 10) === 1000
    }
  },
  methods: {
    ...mapActions('simulations', ['addBudgetSimulation', 'editBudgetSimulation']),
    formatAmount: formatAmount,
    formatFullAmount: formatFullAmount,
    closeDialog () {
      this.$emit('close-dialog')
    },
    channelLabel (channel) {
      const pretty = this.client.pretty_names && this.client.pretty_names.media
      return (pretty && pretty[channel]) || channel
    },
    channelColor (channel) {
      return (this.client.channelColors && this.client.channelColors[channel]) || '#9e9e9e'
    },
    formatDate (date) {
      return date ? moment(date).format('ll') : ''
    },
    // A figure that cannot be read, or is not positive, puts the field back to what it showed.
    setReference (value) {
      const amount = parseAmount(value)
      if (amount > 0) {
        this.referenceBudget = Math.round(amount)
        this.referenceTouched = true
      }
      this.formKey++
    },
    useHistoricalBudget () {
      this.referenceBudget = this.historical.total
      this.referenceTouched = false
      this.formKey++
    },
    setShare (channel, value) {
      this.mixTouched = true
      this.$set(this.mix, channel, value === '' ? 0 : Number(value))
    },
    // Each historical share is rounded on its own, so together they can come to 99.99% or 100.01%;
    // the prefilled mix is evened out to exactly 100%. A mix the user types is left as typed.
    useHistoricalMix () {
      const historicalShares = {}
      this.channelNames.forEach(channel => {
        historicalShares[channel] = this.historical.mix[channel] || 0
      })
      const normalized = normalizeMix(historicalShares)
      const mix = {}
      this.channelNames.forEach(channel => {
        mix[channel] = normalized[channel] || 0
      })
      this.mix = mix
      this.mixTouched = false
    },
    create () {
      this.error = ''
      if (!this.name || !this.name.trim()) {
        this.error = this.$t('components.budgetSimulator.CreateBudgetSimulation.nameRequired')
        return
      }
      if (this.periods.length === 0) {
        this.error = this.$t('components.budgetSimulator.CreateBudgetSimulation.noPeriods')
        return
      }
      if (!(Number(this.referenceBudget) > 0)) {
        this.error = this.$t('components.budgetSimulator.CreateBudgetSimulation.referenceRequired')
        return
      }
      if (this.grid.length < 2) {
        this.error = this.$t('components.budgetSimulator.CreateBudgetSimulation.gridTooSmall')
        return
      }
      if (this.mixMode === 'frozen' && !this.mixValid) {
        this.error = this.$t('components.budgetSimulator.CreateBudgetSimulation.mixMustAddUp')
        return
      }
      const typed = {}
      this.channelNames.forEach(channel => {
        typed[channel] = Math.max(0, Number(this.mix[channel]) || 0)
      })
      const normalized = this.mixMode === 'frozen' ? normalizeMix(typed) : typed
      const mix = {}
      this.channelNames.forEach(channel => {
        mix[channel] = normalized[channel] || 0
      })
      const budgetSimulation = {
        type: 'budget_simulation',
        name: this.name.trim(),
        date_from: this.dateFrom,
        date_to: this.dateTo,
        mix_mode: this.mixMode,
        reference_budget: Number(this.referenceBudget),
        grid: this.grid,
        media_plan_frequency: this.modelFrequency
      }
      if (this.mixMode === 'frozen') {
        budgetSimulation.mix = mix
      }
      // Editing keeps who the Budget Simulator is shared with.
      if (this.edit && this.base && this.base.showToExternalUsers) {
        budgetSimulation.showToExternalUsers = true
      }
      const points = buildPoints({
        ...budgetSimulation,
        mix: mix,
        spend: this.spend,
        periods: this.periods
      })
      this.saving = true
      const save = this.edit ? this.editBudgetSimulation : this.addBudgetSimulation
      save({
        id: this.edit ? this.base.id : undefined,
        client: this.client.id,
        user: `${this.user.firstName} ${this.user.lastName}`,
        userId: this.user.uid,
        budgetSimulation: budgetSimulation,
        points: points
      }).then(id => {
        this.saving = false
        this.$emit('created', id)
        this.closeDialog()
      }).catch(error => {
        console.error('Error creating budget simulation:', error)
        this.saving = false
        this.error = this.$t('components.budgetSimulator.CreateBudgetSimulation.createFailed')
      })
    }
  },
  watch: {
    // A new key replaces the reference field, so the old one's blur never arrives.
    formKey () {
      this.referenceFocused = false
    },
    // Until the user types a reference budget of their own, it follows the historical spend of the period.
    'historical.total' (total) {
      if (!this.referenceTouched) {
        this.referenceBudget = total
        this.formKey++
      }
    },
    // Until the user edits a share, the Frozen mix follows the historical mix of the period.
    historical () {
      if (!this.mixTouched) {
        this.useHistoricalMix()
      }
    }
  },
  created () {
    if (this.base) {
      this.name = this.base.name || ''
      this.dateFrom = this.base.date_from || ''
      this.dateTo = this.base.date_to || ''
      this.mixMode = this.base.mix_mode || null
      if (this.base.reference_budget) {
        this.referenceBudget = this.base.reference_budget
        this.referenceTouched = true
      }
      // A clone or an edit starts from the grid and mix of the original.
      const grid = this.base.grid || []
      if (grid.length > 1) {
        this.minPct = grid[0].pct
        this.maxPct = grid[grid.length - 1].pct
        this.points = Math.min(grid.length, MAX_GRID_POINTS)
      }
      if (this.base.mix) {
        const mix = {}
        this.channelNames.forEach(channel => {
          mix[channel] = Number(this.base.mix[channel]) || 0
        })
        this.mix = mix
        this.mixTouched = true
      }
    }
    this.$nextTick(() => {
      if (!this.referenceTouched) {
        this.referenceBudget = this.historical.total
      }
      if (!this.mixTouched) {
        this.useHistoricalMix()
      }
    })
  }
}
